import * as AppPics from 'assets/images';
import { AppButton } from 'components';
import DOMPurify from 'dompurify';
import useComments from 'hooks/API/GET/useComments';
import useManageComments from 'hooks/API/useManageComments';
import useReviewComment from 'hooks/API/useReviewComment';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { useState, useEffect, createRef, useContext } from 'react';
import { contentErrors, questionIssues, ROLES } from 'utilities/constants';
// import { DropdownMenu, DropdownItem } from 'reactstrap';
import { AiFillEdit, AiOutlineDelete } from 'react-icons/ai';
import { TiTick, TiTimes } from 'react-icons/ti';
// import VDots from 'components/DropDowns/VDots';
import Editor from 'components/CKEditor/Editor';
import Alert from '@mui/material/Alert';
import { notifier } from 'components/Toaster';
import dayjs from 'dayjs';
import CommentBoxParent from 'pages/manage-topics/manage-contents/PreviewSection/CommentBoxParent';
import QuestionIssuesList from './QuestionIssuesList';
import useQueryParams from 'hooks/useQueryParams';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { UserContext } from 'context/UserContext';
import PickIssuesList from './PickIssuesList';
import Icons from 'assets/Icons';
import avatarSvg from 'pages/Layouts/Admin/avatar.svg';
import CommentBoxDelete from 'pages/manage-topics/manage-contents/PreviewSection/CommentBoxDelete';

function MindMapCommentDropBox({ open, data = {}, close }) {
  const { quizID, courseID, chapterID, classID } = useParams();
  const { _id: apiHash } = data;

  const { userInfo = {} } = useContext(UserContext);
  const { key } = userInfo?.data ?? {};

  const queryParams = useQueryParams();
  const paperId = queryParams.get('paperId');

  const apiBodyProps = {
    class: classID,
    course: courseID,
    chapter: chapterID !== 'undefined' ? chapterID : undefined,
    quiz: quizID,
    question: undefined,
    paperId,
  };

  const ref = createRef();
  useOnClickOutside(ref, () => close());

  const [issueType, setIssueType] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewDetails, setReviewDetails] = useState({});
  const [reply, setReply] = useState('');
  const [editReply, setEditReply] = useState({});
  const [errorParts, setErrorParts] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, data: {} });

  const { isLoading, data: response = {} } = useComments({ enabled: !!apiHash, hashID: apiHash });

  const { isLoading: isAdding, mutate } = useReviewComment();

  const manageReply = useManageComments();

  useEffect(() => {
    if (editReply?.replyID) {
      setReply(editReply.text);
    }
  }, [editReply]);

  useEffect(() => {
    if (response?.data?._id) {
      setReviewDetails(response.data);

      if (Array.isArray(response.data?.issueType)) {
        const _arr = [];
        for (const [key, value] of Object.entries(contentErrors)) {
          if (response.data?.issueType?.includes(key)) _arr.push({ value: key, label: value });
        }
        setIssueType(_arr);
      }
      if (typeof response.data?.issueType === 'string' && response.data?.issueType) {
        setIssueType([
          { value: response.data?.issueType, label: contentErrors[response.data?.issueType] },
        ]);
      }

      setReviewComment(response.data?.reviewComment || '');
      if (Array.isArray(response.data?.reportedIn)) {
        const array = [];
        for (const [key, value] of Object.entries(questionIssues)) {
          if (response.data?.reportedIn.includes(key)) array.push({ label: value, value: key });
        }

        setErrorParts(array);
      }
    }
  }, [response?.data]);

  function clear() {
    setIssueType([]);
    setReviewComment('');
    handleClose();
  }

  function handleClose() {
    if (isLoading) return;
    close();
  }

  function handleSave(args = {}) {
    if (isLoading) return;

    const hasNoIssues = issueType.every((item) => item.value !== 'no_issues');
    if (hasNoIssues && !`${reviewComment}`.trim()) return;

    const needConfirmation = !hasNoIssues;
    if (needConfirmation) {
      const hasConfirmed = window.confirm('Are you sure you there are no issues?');
      if (!hasConfirmed) return;
    }

    let apiData = {
      ...apiBodyProps,
      lessonType: 'mindmap',
      issueType: issueType.map((item) => item.value),
      reviewComment: DOMPurify.sanitize(reviewComment),
      reportedIn: errorParts?.map?.((er) => er.value) ?? [],
      denyUpdateCall: reviewDetails?.denyUpdateCall || false,
      type: reviewDetails?._id ? 'UPDATE' : 'CREATE',
      hasBlochHashComment: !!reviewDetails?.selectedBlock?._id,
      _id: reviewDetails?._id,
      ...args,
    };

    // Execute this "if"; green tick is clicked
    if (args?.flag === 'EDIT_BUTTON_CLICK') {
      if (response?.data?.markResolved === true) apiData.resolved = true;
      if (response?.data?.markResolved !== true && response?.data?.markAsDone) {
        // do not remove colors-span and update/get not API not gone
        delete apiData.resolved;
        apiData.respondentResolve = true;
      }
    }

    if (args?.flag === 'MARK_AS_NOT_RESOLVED') {
      if (!window.confirm('Are you sure you want to Mark as unresolved?')) return;

      apiData = {
        _id: reviewDetails?._id,
        type: 'MARK_AS_NOT_RESOLVED',
        flag: 'MARK_AS_NOT_RESOLVED',
      };
    }
    if (args?.flag === 'EDIT_BUTTON_CLICK_UNRESOLVED') {
      if (response?.data?.markAsDone) apiData.respondentUnResolve = true;
      else apiData.markUnResolved = true;
      apiData = {
        _id: reviewDetails?._id,
        ...apiData,
        type: 'EDIT_BUTTON_CLICK_UNRESOLVED',
        flag: 'EDIT_BUTTON_CLICK_UNRESOLVED',
      };
    }
    manageReply.mutate(apiData, {
      onSuccess(successData, variables) {
        if (variables?.type === 'DELETE') {
          notifier.success('Comment deleted successfully');
        }
        if (['UPDATE', 'CREATE', 'MARK_AS_NOT_RESOLVED'].includes(variables?.type)) {
          notifier.success('Comment posted successfully');
        }

        clear();
      },
    });
  }

  function handleReply(args = {}) {
    if (isAdding) return;
    if (!reviewDetails?._id) return;
    if (!`${reply}`.trim()) return;

    const apiData = {
      ...args,
      _id: reviewDetails._id,
      comment: `${reply}`.trim(),
    };

    mutate(apiData, {
      onSuccess() {
        setReply('');
        notifier.success('Reply posted successfully');
      },
    });
  }

  function modifyReply(args = {}) {
    if (isAdding) return;

    const apiData = {
      ...args,
      _id: reviewDetails._id,
    };

    mutate(apiData, {
      onSuccess() {
        setEditReply({});
        setReply('');
        setDeleteModal({ isOpen: false, data: {} });
      },
    });
  }

  const isSE = key === ROLES.SUBJECT_EXPERT;

  let doNorShowResolveBtn = isSE && response?.data?.respondentResolve;
  if (response?.data?.resolved && [ROLES.QC, ROLES.TUTOR].includes(key)) doNorShowResolveBtn = true;

  const __issues = [];
  if (!editMode && apiHash) {
    for (const [key, value] of Object.entries(contentErrors)) {
      if (Array.isArray(reviewDetails?.issueType) && reviewDetails?.issueType?.includes(key)) {
        __issues.push({ label: value, value: key });
      }
    }
  }

  let issueLabel = `RAISE AN ISSUE`;
  let resolvedCls = '';

  if (response?.data?.respondent === 'SUBJECT_EXPERT' && !response?.data?.respondentResolve) {
    issueLabel = `ISSUE UNRESOLVED`;
    resolvedCls = 'unresolved_se';
  }
  if (response?.data?.respondent === 'SUBJECT_EXPERT' && response?.data?.respondentResolve) {
    issueLabel = `SE MARKED COMPLETE`;
    resolvedCls = '';
  }
  if (response?.data?.resolved) {
    issueLabel = `ISSUE RESOLVED`;
    resolvedCls = '';
  }

  return (
    <>
      <CommentBoxParent ref={ref} open={open}>
        <div className="modal-dialog modal-dialog-centered uploaddialog side-modal">
          <div className="modal-content">
            {deleteModal.isOpen && (
              <CommentBoxDelete
                closeModal={() => {
                  setDeleteModal({ isOpen: false, data: {} });
                }}
                data={deleteModal.data}
                handleDelete={() => {
                  deleteModal.data?.handleDelete?.();
                }}
              />
            )}

            <div className="modal-head">
              <AppButton variantClass="btn invisible">
                <Icons.expandIconV1 />
              </AppButton>
              <h2 className="popuptitle">{issueLabel}</h2>

              <AppButton variantClass="btn" onClick={close}>
                <Icons.closeIcon />
              </AppButton>
            </div>
            <div className="modal-body p-0 __review__blocks_quotes">
              <div className="container p-0">
                <div className="blockquote">
                  <blockquote>About this Mind Tree</blockquote>

                  {/* START OF EXISTING COMMENT (from API): SELECTED TEXT  */}
                  {reviewDetails?._id && reviewDetails?.commentText && (
                    <blockquote>
                      <p
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(reviewDetails?.commentText || ''),
                        }}
                      ></p>{' '}
                    </blockquote>
                  )}
                </div>
                {/* START OF EXISTING COMMENT (from API):  SELECTED TEXT  */}

                {reviewDetails?.respondentBy?.name && (
                  <Alert severity="success">
                    {reviewDetails?.respondentBy?.name} has responded to this comment.
                  </Alert>
                )}

                {(editMode || !apiHash) && (
                  <div className="commentsection">
                    <div className="w-100 mt-3 select-issue">
                      <PickIssuesList
                        isMulti
                        values={issueType}
                        handleChange={(data) => {
                          if (Array.isArray(data)) {
                            const hasNoIssues = data.find((item) => item.value === 'no_issues');

                            if (hasNoIssues) setIssueType([hasNoIssues]);
                            if (!hasNoIssues) setIssueType(data);
                          }
                        }}
                      />
                    </div>
                    <div className="w-100 mt-3  -negate-padding-20">
                      <Editor
                        config={{
                          toolbar: [{ items: ['Bold', 'Italic', 'Image'] }],
                          toolbarGroups: [],
                        }}
                        data={{
                          value: reviewComment || '',
                          onChange: (editorData) => {
                            setReviewComment(editorData);
                          },
                        }}
                      />
                    </div>
                    {/* <AppButton
                      variantClass="btn btn-primary savebutton mt-3"
                      onClick={() => {
                        handleSave();
                      }}
                    >
                      Save
                    </AppButton> */}
                  </div>
                )}
                {!editMode && apiHash && (
                  <div className=" m-0 p-0 bg-transparent mt-3 ___edpod_rov__Commen">
                    <div className="row m-0 p-0 ">
                      <div className="col-12 p-0 m-0 ">
                        <div className="card border-0 rounded">
                          <div className="card-header p-1 bg-light border border-top-0 border-left-0 border-right-0 flex-btns -addpadd-20-lr">
                            <div className="__metaInvof">
                              <div className="__issueShort">
                                <h4> Issue Genre: </h4>
                                <span>
                                  {' '}
                                  {issueType.map((item, i) => {
                                    return (
                                      <button
                                        key={i + item.label}
                                        type="button"
                                        class="btn btn-sm btn-outline-danger mx-1"
                                      >
                                        {item.label}
                                      </button>
                                    );
                                  })}{' '}
                                </span>
                              </div>
                              <div className="__issueDefn">
                                <h4> Issue Brief: </h4>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(reviewDetails?.reviewComment),
                                  }}
                                />
                              </div>
                              {errorParts?.length > 0 && (
                                <div className="__issueDefn">
                                  <h4> Errors in: </h4>

                                  <div className="__issueShort">
                                    <strong>
                                      {errorParts?.map?.((item, i) => {
                                        return (
                                          <button
                                            key={i + '-' + item.label}
                                            type="button"
                                            class="btn btn-sm btn-outline-info mx-1"
                                          >
                                            {item.label}
                                          </button>
                                        );
                                      })}
                                    </strong>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="__Iss_btn"></div>
                          </div>

                          {/* START :: BUTTONS */}
                          <div class="d-flex my-4 -addpadd-20-lr d-flex-wrap btn-mrt-10">
                            <AppButton
                              className={`btn-plane g--tint g-tinted-gradient mar-rgt-12 ${
                                doNorShowResolveBtn ? 'd-none' : ''
                              }`}
                              onClick={() =>
                                handleSave({ resolved: true, flag: 'EDIT_BUTTON_CLICK' })
                              }
                            >
                              {response?.data?.markAsDone ? 'Mark as Done' : 'Resolve'}{' '}
                              <img
                                src={AppPics.SEDONE}
                                alt="resolve-btn"
                                title={response?.data?.markAsDone ? 'Complete' : 'Resolve'}
                                height={16}
                                width={16}
                              />
                            </AppButton>
                            <AppButton
                              className={`btn-plane g--tint g-tinted-gradient-reverse ${
                                doNorShowResolveBtn ? 'd-none' : ''
                              }`}
                              onClick={() =>
                                handleSave({
                                  resolved: false,
                                  flag: 'EDIT_BUTTON_CLICK_UNRESOLVED',
                                })
                              }
                            >
                              {response?.data?.markAsDone ? 'Mark Not Done' : 'Mark UNRESOLVED'}{' '}
                              <img
                                src={AppPics.SEUNDONE}
                                alt="resolve-btn"
                                title={
                                  response?.data?.markAsDone ? 'Mark Not Done' : 'Mark UNRESOLVED'
                                }
                                height={16}
                                width={16}
                              />
                            </AppButton>
                            {/* {key !== ROLES.SUBJECT_EXPERT && !doNorShowResolveBtn && (
                              <AppButton
                                className="btn-plane g--tint mx-4"
                                onClick={() => handleSave({ flag: 'MARK_AS_NOT_RESOLVED' })}
                              >
                                Mark as NOT RESOLVED <FaTimes color="red" />
                              </AppButton>
                            )} */}

                            {(response?.data?.canEdit || response?.data?.canDelete) && (
                              <>
                                {response?.data?.canEdit && (
                                  <AppButton
                                    variantClass="btn-plane mx-4"
                                    onClick={() => {
                                      setEditMode(true);
                                    }}
                                  >
                                    Edit issue
                                  </AppButton>
                                )}
                                {response?.data?.canDelete && (
                                  <AppButton
                                    variantClass="btn-plane p--tint mx-4"
                                    onClick={() => {
                                      setDeleteModal({
                                        isOpen: true,
                                        data: {
                                          apiPayload: { type: 'DELETE', _id: reviewDetails?._id },
                                          headingText: 'Delete Issue',
                                          handleDelete() {
                                            if (isLoading) return;

                                            mutate(
                                              { type: 'DELETE', _id: reviewDetails?._id },
                                              {
                                                onSuccess() {
                                                  notifier.success('Comment deleted successfully');

                                                  return;
                                                },
                                                onSettled() {
                                                  setDeleteModal({ isOpen: false, data: {} });
                                                  close();
                                                },
                                              }
                                            );
                                          },
                                        },
                                      });
                                    }}
                                  >
                                    Delete issue
                                  </AppButton>
                                )}
                              </>
                            )}
                          </div>
                          {/* END :: BUTTONS */}

                          {/* START :: COMMENTS & ACTIVITY */}
                          <div class="card text-center app-l-comments-wrapper">
                            <div class="card-header">COMMENTS & ACTIVITY</div>
                            <div class="card-body">
                              <ul class="list-group list-group-flush">
                                {reviewDetails?.replyComment?.map?.((item, i) => {
                                  if (item.commentType === 'activity') {
                                    return (
                                      <li class="list-group-item" key={item._id}>
                                        <img src={avatarSvg} alt="Avatar" height={32} width={32} />

                                        <div>
                                          {item.text === 'created_issue' && (
                                            <span>
                                              {item.createdBy?.name}{' '}
                                              {i === 0
                                                ? `raised ${
                                                    __issues && __issues.length === 1
                                                      ? `an`
                                                      : `${__issues.length}`
                                                  } issue${
                                                    __issues && __issues.length === 1 ? `` : `s`
                                                  }`
                                                : `added a new comment`}
                                              .
                                            </span>
                                          )}
                                          {item.text === 'updated_issue' && (
                                            <span>{item.createdBy?.name} updated the issue.</span>
                                          )}
                                          {item.text === 'deleted_issue' && (
                                            <span>{item.createdBy?.name} deleted the issue.</span>
                                          )}
                                          {item.text === 'marked_resolved' && (
                                            <span>
                                              {item.createdBy?.name} marked this issue as resolved.
                                            </span>
                                          )}
                                          {item.text === 'respondent_responded' && (
                                            <span>{item.createdBy?.name} marked as done.</span>
                                          )}
                                          {item.text === 'un_done' && (
                                            <span>
                                              {item.createdBy?.name} marked this issue as not done.
                                            </span>
                                          )}{' '}
                                          {item.text === 'un_resolved' && (
                                            <span>
                                              {item.createdBy?.name} marked this issue as NOT
                                              RESOLVED.
                                            </span>
                                          )}
                                          <span>
                                            {/* <em>{item?.createdBy?.name}</em>{' '} */}
                                            {dayjs(item.createdOn).format('DD MMM YYYY, h:mm a')}
                                          </span>
                                        </div>
                                      </li>
                                    );
                                  }
                                  return (
                                    <li class="list-group-item" key={item._id}>
                                      <img src={avatarSvg} alt="Avatar" height={32} width={32} />
                                      <div className="position-relative">
                                        {!item.freeze && (
                                          <section
                                            className="position-absolute"
                                            style={{ bottom: '-18px', left: 0 }}
                                          >
                                            <span
                                              className="displayPointer"
                                              role="button"
                                              onClick={() => {
                                                setEditReply({
                                                  replyID: item._id,
                                                  text: item.text,
                                                });
                                              }}
                                            >
                                              <AiFillEdit color="primary" />
                                            </span>
                                            <span
                                              className="displayPointer mx-2"
                                              role="button"
                                              onClick={() => {
                                                setDeleteModal({
                                                  isOpen: true,
                                                  data: {
                                                    headingText: 'Delete Comment',
                                                    handleDelete() {
                                                      modifyReply({
                                                        type: 'DELETE',
                                                        configs: {
                                                          params: { replyID: item._id },
                                                        },
                                                      });
                                                    },
                                                  },
                                                });
                                              }}
                                            >
                                              <AiOutlineDelete color="red" />
                                            </span>
                                          </section>
                                        )}
                                        <small
                                          className="__replyComments"
                                          dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(item.text),
                                          }}
                                        ></small>
                                        <span>
                                          <em>{item?.createdBy?.name}</em>{' '}
                                          {dayjs(item.createdOn).format('DD MMM YYYY, h:mm a')}
                                        </span>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </div>
                          {/* END :: COMMENTS & ACTIVITY */}

                          {/* <div className="card bg-sohbet border-0 m-0 p-4 px-5 __Review__secc"></div> */}
                          {/* <div className="w-100 card-footer p-0 bg-light border border-bottom-0 border-left-0 border-right-0">
                            <form className="m-0 p-0" autocomplete="off">
                              <div className="row m-0 p-0">
                                <div className="col-9 m-0 p-1">
                                  <Editor
                                    config={{
                                      toolbar: [{ items: ['Bold', 'Italic', 'Image'] }],
                                      toolbarGroups: [],
                                    }}
                                    data={{
                                      value: reply || '',
                                      onChange: (editorData) => {
                                        setReply(editorData);
                                      },
                                    }}
                                  />
                                </div>
                                <div className="col-3 m-0 p-1">
                                  {!editReply?.replyID && (
                                    <AppButton
                                      variantClass="btn btn-outline-secondary rounded border w-100  replybtn"
                                      title="Click to reply"
                                      onClick={() => handleReply()}
                                    >
                                      Reply
                                    </AppButton>
                                  )}
                                  {editReply?.replyID && (
                                    <>
                                      <AppButton
                                        variantClass="btn btn-success mx-2"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => {
                                          modifyReply({
                                            type: 'UPDATE',
                                            replyID: editReply.replyID,
                                            reply,
                                          });
                                        }}
                                      >
                                        <TiTick />
                                      </AppButton>
                                      <AppButton
                                        variantClass="btn btn-danger mx-2"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => {
                                          setEditReply({});
                                          setReply('');
                                        }}
                                      >
                                        <TiTimes />
                                      </AppButton>
                                    </>
                                  )}
                                </div>
                              </div>
                            </form>
                          </div> */}
                          <div className="w-100  -negate-padding-20">
                            <Editor
                              config={{
                                toolbar: [{ items: ['Bold', 'Italic', 'Image'] }],
                                toolbarGroups: [],
                              }}
                              data={{
                                value: reply || '',
                                onChange: (editorData) => {
                                  setReply(editorData);
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {(editMode || !apiHash) && (
                <AppButton
                  variantClass="btn"
                  onClick={() => {
                    handleSave();
                  }}
                >
                  Save
                </AppButton>
              )}
              {apiHash && !editReply?.replyID && (
                <AppButton variantClass="btn " title="Click to reply" onClick={() => handleReply()}>
                  Reply
                </AppButton>
              )}
              {editReply?.replyID && (
                <>
                  <AppButton
                    variantClass="btn"
                    style={{ fontSize: '12px' }}
                    onClick={() => {
                      modifyReply({
                        type: 'UPDATE',
                        replyID: editReply.replyID,
                        reply,
                      });
                    }}
                  >
                    <TiTick />
                  </AppButton>
                  <AppButton
                    variantClass="btn"
                    style={{ fontSize: '12px' }}
                    onClick={() => {
                      setEditReply({});
                      setReply('');
                    }}
                  >
                    Cancel
                    {/* <TiTimes /> */}
                  </AppButton>
                </>
              )}
            </div>
          </div>
        </div>
      </CommentBoxParent>
    </>
  );
}

export default MindMapCommentDropBox;
